/**
 * PersonalPerformanceWidget.js
 *
 * Technician Dashboard: Personal Performance Widget
 * Rolling last-7-days metrics (UTC) with Overall / Complaints / Maintenance tabs.
 *
 * Props:
 * - complaints, devices, workHours
 * - onNavigateToComplaints, onNavigateToDevices, onNavigateToWorkLogs
 */

import { useState, useEffect } from 'react';

const PersonalPerformanceWidget = ({
  complaints = [],
  devices = [],
  workHours = 0,
  onNavigateToComplaints,
  onNavigateToDevices,
  onNavigateToWorkLogs,
}) => {
  const [activeTab, setActiveTab] = useState('overall'); // 'overall', 'complaints', 'maintenance'
  const [performanceData, setPerformanceData] = useState({
    overall: {
      completedThisWeek: 0,
      avgCompletionTime: 0,
      onTimeCompletionRate: 0,
      totalWorkHours: workHours,
      complaintsResolved: 0,
      maintenanceCompleted: 0,
      complaintHours: 0,
      maintenanceHours: 0,
      trend: 'stable',
      trendPercent: 0
    },
    complaints: {
      resolvedThisWeek: 0,
      bySeverity: {
        critical: 0,
        major: 0,
        minor: 0
      },
      avgResolutionTime: 0,
      onTimeCompletionRate: 0,
      resolutionTimeBySeverity: {
        critical: 0,
        major: 0,
        minor: 0
      },
      onTimeRateBySeverity: {
        critical: 0,
        major: 0,
        minor: 0
      },
      trend: 'stable',
      trendPercent: 0,
      avgAssignToAckHours: 0,
      avgAckToStartHours: 0,
    },
    maintenance: {
      completedThisWeek: 0,
      byType: {
        ppm: 0,
        calibration: 0,
        compliance: 0
      },
      avgCompletionTime: 0,
      totalMaintenanceHours: 0,
      onTimeCompletionRate: 0,
      onTimeByType: {
        ppm: 0,
        calibration: 0,
        compliance: 0
      },
      devicesMaintained: 0,
      previousWeekCompleted: 0,
      trend: 'stable',
      trendPercent: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getThisWeekRange = () => {
    // IMPORTANT: Backend stores and reasons about timestamps in UTC.
    // Define the window using UTC day boundaries to avoid user-local timezone drift.
    // Window: today (UTC) + previous 6 days = 7 UTC calendar days.
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = now.getUTCMonth();
    const d = now.getUTCDate();

    const end = new Date(Date.UTC(y, m, d, 23, 59, 59, 999));
    const start = new Date(Date.UTC(y, m, d, 0, 0, 0, 0));
    start.setUTCDate(start.getUTCDate() - 6);

    return { start, end };
  };

  const getPreviousWeekRange = () => {
    const { start: thisStart } = getThisWeekRange();
    const end = new Date(thisStart);
    end.setMilliseconds(end.getMilliseconds() - 1); // end of day before this range starts

    const start = new Date(thisStart);
    start.setUTCDate(start.getUTCDate() - 7); // previous 7 UTC calendar days
    start.setUTCHours(0, 0, 0, 0);

    return { start, end };
  };

  const getThisWeekDateRangeLabel = () => {
    const { start, end } = getThisWeekRange();

    const monthDay = (d) =>
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
    const year = (d) => d.getFullYear();

    // If crossing years, include year on both ends; otherwise omit for compactness
    if (year(start) !== year(end)) {
      const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
      return `${fmt(start)} – ${fmt(end)}`;
    }

    return `${monthDay(start)} – ${monthDay(end)}`;
  };

  useEffect(() => {
    calculateAllMetrics();
  }, [complaints, devices, workHours]);

  const calculateAllMetrics = () => {
    try {
      setLoading(true);

      const complaintMetrics = calculateComplaintMetrics();
      const maintenanceMetrics = calculateMaintenanceMetrics();
      const overallMetrics = calculateOverallMetrics(complaintMetrics, maintenanceMetrics);

      setPerformanceData({
        overall: overallMetrics,
        complaints: complaintMetrics,
        maintenance: maintenanceMetrics
      });

      setLoading(false);
    } catch (err) {
      console.error('Error calculating performance metrics:', err);
      setError('Failed to calculate metrics');
      setLoading(false);
    }
  };

  // Helper function to safely parse hours spent
  const parseHoursSpent = (hoursValue) => {
    if (!hoursValue) return 0;

    const hours = parseFloat(hoursValue);

    // Return 0 if result is NaN or negative
    if (isNaN(hours) || hours < 0) {
      console.warn('Invalid hours_spent value:', hoursValue);
      return 0;
    }

    return hours;
  };

  // Fuzzy string matching - calculates similarity between two strings (0-1)
  const calculateStringSimilarity = (str1, str2) => {
    if (!str1 || !str2) return 0;

    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();

    // Extract key words (remove common words like "the", "a", "is", etc.)
    const stopWords = new Set(['the', 'a', 'an', 'is', 'was', 'are', 'been', 'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'can', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'from', 'of', 'for', 'with', 'by']);

    const words1 = s1.split(/\s+/).filter(w => w.length > 2 && !stopWords.has(w));
    const words2 = s2.split(/\s+/).filter(w => w.length > 2 && !stopWords.has(w));

    if (words1.length === 0 || words2.length === 0) return 0;

    // Count matching words
    let matches = 0;
    words1.forEach(w1 => {
      if (words2.some(w2 => w2.includes(w1) || w1.includes(w2))) {
        matches++;
      }
    });

    // Similarity = matching words / max(len1, len2)
    return matches / Math.max(words1.length, words2.length);
  };

  // Check if two titles represent the same issue (similarity threshold: 0.6)
  const isSameIssue = (title1, title2) => {
    return calculateStringSimilarity(title1, title2) >= 0.6;
  };

  const calculateComplaintMetrics = () => {
    const { start: weekStart, end: weekEnd } = getThisWeekRange();
    const { start: prevWeekStart, end: prevWeekEnd } = getPreviousWeekRange();

    // Filter resolved complaints from this week (status = 'closed' with closed_at timestamp)
    const resolvedThisWeek = complaints.filter(c => {
      if (c.status !== 'closed') return false;
      if (!c.closed_at) return false;
      const resolvedDate = new Date(c.closed_at);
      if (isNaN(resolvedDate.getTime())) return false;
      return resolvedDate >= weekStart && resolvedDate <= weekEnd;
    });

    // Filter assigned complaints from this week (for response time calculation)
    // These are complaints that were assigned to the engineer this week
    const assignedThisWeek = complaints.filter(c => {
      // Include both in-progress and closed complaints (both were assigned this week)
      if (!['in_progress', 'closed'].includes(c.status)) return false;
      // Check if complaint has status_history data
      if (!c.status_history || !Array.isArray(c.status_history)) return false;

      // Find the assignment event
      const assignmentEvent = c.status_history.find(e => e.event_type === 'assignment');
      if (!assignmentEvent || !assignmentEvent.changed_at) return false;

      const assignedDate = new Date(assignmentEvent.changed_at);
      if (isNaN(assignedDate.getTime())) return false;
      return assignedDate >= weekStart && assignedDate <= weekEnd;
    });

    // Separate resolved complaints by severity
    const bySeverity = {
      critical: resolvedThisWeek.filter(c => c.severity === 'critical').length,
      major: resolvedThisWeek.filter(c => c.severity === 'major').length,
      minor: resolvedThisWeek.filter(c => c.severity === 'minor').length
    };

    // Define severity groups for reuse in multiple calculations
    const severityGroups = {
      critical: resolvedThisWeek.filter(c => c.severity === 'critical'),
      major: resolvedThisWeek.filter(c => c.severity === 'major'),
      minor: resolvedThisWeek.filter(c => c.severity === 'minor')
    };

    // Calculate average ticket age (from assignment to closure)
    let totalTicketAgeHours = 0;
    let avgTicketAgeHours = 0;
    let avgTicketAgeDays = 0;
    let ticketsWithAssignmentData = 0;

    // For each resolved complaint, calculate age from assignment to closure
    // Note: assignment timestamp comes from complaint_status_history table
    // If assignment data not available, we'll use created_at as fallback
    if (resolvedThisWeek.length > 0) {
      resolvedThisWeek.forEach(complaint => {
        const closedDate = new Date(complaint.closed_at);

        // Use created_at as the base timestamp (when reported)
        // In a future enhancement, we could fetch status_history to get actual assignment time
        const startDate = new Date(complaint.created_at);

        // Check if dates are valid
        if (!isNaN(startDate.getTime()) && !isNaN(closedDate.getTime())) {
          const ageHours = (closedDate - startDate) / (1000 * 60 * 60);
          if (ageHours >= 0) {
            totalTicketAgeHours += ageHours;
            ticketsWithAssignmentData++;
          }
        }
      });

      if (ticketsWithAssignmentData > 0) {
        avgTicketAgeHours = Math.round(totalTicketAgeHours / ticketsWithAssignmentData * 10) / 10;
        avgTicketAgeDays = Math.round((totalTicketAgeHours / 24) / ticketsWithAssignmentData * 10) / 10;
      }
    }

    // Calculate ticket age by severity (from creation to closure)
    const ticketAgeBySevertity = {
      critical: { hours: 0, days: 0 },
      major: { hours: 0, days: 0 },
      minor: { hours: 0, days: 0 }
    };

    Object.keys(severityGroups).forEach(severity => {
      const group = severityGroups[severity];
      if (group.length > 0) {
        let totalSeverityAgeHours = 0;
        let validCount = 0;
        group.forEach(complaint => {
          const startDate = new Date(complaint.created_at);
          const closedDate = new Date(complaint.closed_at);

          // Check if dates are valid
          if (!isNaN(startDate.getTime()) && !isNaN(closedDate.getTime())) {
            const ageHours = (closedDate - startDate) / (1000 * 60 * 60);
            if (ageHours >= 0) {
              totalSeverityAgeHours += ageHours;
              validCount++;
            }
          }
        });

        if (validCount > 0) {
          const avgHours = Math.round(totalSeverityAgeHours / validCount * 10) / 10;
          const avgDays = Math.round((totalSeverityAgeHours / 24) / validCount * 10) / 10;
          ticketAgeBySevertity[severity] = { hours: avgHours, days: avgDays };
        }
      }
    });

    // Calculate average time spent on complaints (hours_spent field)
    let totalHours = 0;
    let avgResolutionTime = 0;

    if (resolvedThisWeek.length > 0) {
      let validComplaintsCount = 0;
      resolvedThisWeek.forEach(complaint => {
        const hours = parseHoursSpent(complaint.hours_spent);
        totalHours += hours;
        if (complaint.hours_spent !== null && complaint.hours_spent !== undefined && complaint.hours_spent !== '') {
          validComplaintsCount++;
        }
      });

      // Calculate average only from complaints with hours_spent recorded
      if (validComplaintsCount > 0) {
        avgResolutionTime = Math.round(totalHours / validComplaintsCount * 10) / 10;
      } else {
        // If no hours_spent recorded, still count total hours / all resolved complaints
        avgResolutionTime = resolvedThisWeek.length > 0
          ? Math.round(totalHours / resolvedThisWeek.length * 10) / 10
          : 0;
      }
    }

    // Calculate resolution time by severity
    const resolutionTimeBySeverity = {
      critical: 0,
      major: 0,
      minor: 0
    };

    Object.keys(severityGroups).forEach(severity => {
      const group = severityGroups[severity];
      if (group.length > 0) {
        let totalSeverityHours = 0;
        let validCount = 0;
        group.forEach(complaint => {
          const hours = parseHoursSpent(complaint.hours_spent);
          totalSeverityHours += hours;
          if (complaint.hours_spent !== null && complaint.hours_spent !== undefined && complaint.hours_spent !== '') {
            validCount++;
          }
        });

        // Calculate average only from complaints with hours_spent recorded
        if (validCount > 0) {
          resolutionTimeBySeverity[severity] = Math.round(totalSeverityHours / validCount * 10) / 10;
        } else {
          // If no hours_spent recorded, still calculate average from all
          resolutionTimeBySeverity[severity] = group.length > 0
            ? Math.round(totalSeverityHours / group.length * 10) / 10
            : 0;
        }
      }
    });

    // Calculate on-time completion
    let onTimeCount = 0;
    resolvedThisWeek.forEach(complaint => {
      if (complaint.due_date) {
        const dueDate = new Date(complaint.due_date);
        const resolvedDate = new Date(complaint.closed_at);
        if (resolvedDate <= dueDate) {
          onTimeCount++;
        }
      } else {
        onTimeCount++;
      }
    });

    const onTimeCompletionRate = resolvedThisWeek.length > 0
      ? Math.round((onTimeCount / resolvedThisWeek.length) * 100)
      : 0;

    // Calculate on-time completion rate by severity
    const onTimeRateBySeverity = {
      critical: 0,
      major: 0,
      minor: 0
    };

    Object.keys(severityGroups).forEach(severity => {
      const group = severityGroups[severity];
      if (group.length > 0) {
        let severityOnTimeCount = 0;
        group.forEach(complaint => {
          if (complaint.due_date) {
            const dueDate = new Date(complaint.due_date);
            const resolvedDate = new Date(complaint.closed_at);
            if (resolvedDate <= dueDate) {
              severityOnTimeCount++;
            }
          } else {
            severityOnTimeCount++;
          }
        });
        onTimeRateBySeverity[severity] = Math.round((severityOnTimeCount / group.length) * 100);
      }
    });

    // Calculate repeat issues (last 30 days, same device + similar title)
    const thirtyDaysAgo = new Date(weekEnd.getTime() - 30 * 24 * 60 * 60 * 1000);
    const last30DaysComplaints = complaints.filter(c => {
      const createdDate = new Date(c.created_at);
      if (isNaN(createdDate.getTime())) return false;
      return createdDate >= thirtyDaysAgo && createdDate <= weekEnd;
    });

    // Group complaints by device
    const complaintsByDevice = {};
    last30DaysComplaints.forEach(complaint => {
      if (!complaintsByDevice[complaint.device_id]) {
        complaintsByDevice[complaint.device_id] = [];
      }
      complaintsByDevice[complaint.device_id].push(complaint);
    });

    // Find repeat issues (same device + similar titles)
    let repeatIssueCount = 0;
    const topRepeatIssues = [];

    Object.keys(complaintsByDevice).forEach(deviceId => {
      const deviceComplaints = complaintsByDevice[deviceId];
      if (deviceComplaints.length > 1) {
        // Find groups of similar issues
        const issueClusters = [];
        const used = new Set();

        deviceComplaints.forEach((complaint, idx) => {
          if (used.has(idx)) return;

          const cluster = [complaint];
          used.add(idx);

          // Find all similar complaints
          deviceComplaints.forEach((other, otherIdx) => {
            if (used.has(otherIdx)) return;
            if (isSameIssue(complaint.title, other.title)) {
              cluster.push(other);
              used.add(otherIdx);
            }
          });

          // Only count as repeat if 2+ complaints in cluster
          if (cluster.length > 1) {
            issueClusters.push({
              title: complaint.title,
              count: cluster.length,
              deviceId: deviceId
            });
            repeatIssueCount += cluster.length - 1; // Add excess count only
          }
        });

        topRepeatIssues.push(...issueClusters);
      }
    });

    // Sort by frequency and get top 3
    topRepeatIssues.sort((a, b) => b.count - a.count);
    const topRepeatIssuesDisplay = topRepeatIssues.slice(0, 3);

    // Response time: assignment → start, with legs assignment → ack and ack → start
    const resolveResponseTimestamps = (complaint) => {
      const history = Array.isArray(complaint.status_history) ? complaint.status_history : [];
      const assignmentEvent = history.find((e) => e.event_type === 'assignment');
      const ackEvent = history.find(
        (e) => e.event_type === 'status_change' && e.new_value === 'acknowledged'
      );
      const inProgressEvent = history.find(
        (e) => e.event_type === 'status_change' && e.new_value === 'in_progress'
      );

      const assignedAt = assignmentEvent?.changed_at
        ? new Date(assignmentEvent.changed_at)
        : null;

      let ackAt = ackEvent?.changed_at ? new Date(ackEvent.changed_at) : null;
      if ((!ackAt || Number.isNaN(ackAt.getTime())) && complaint.acknowledged_at) {
        ackAt = new Date(complaint.acknowledged_at);
      }

      let startAt = inProgressEvent?.changed_at ? new Date(inProgressEvent.changed_at) : null;
      if ((!startAt || Number.isNaN(startAt.getTime())) && complaint.closed_at) {
        startAt = new Date(complaint.closed_at);
      }

      const valid = (d) => d instanceof Date && !Number.isNaN(d.getTime());
      return {
        assignedAt: valid(assignedAt) ? assignedAt : null,
        ackAt: valid(ackAt) ? ackAt : null,
        startAt: valid(startAt) ? startAt : null,
      };
    };

    let totalResponseMinutes = 0;
    let responseCount = 0;
    let totalAssignToAckMinutes = 0;
    let assignToAckCount = 0;
    let totalAckToStartMinutes = 0;
    let ackToStartCount = 0;
    const responseMinutesBySeverity = {
      critical: { total: 0, count: 0 },
      major: { total: 0, count: 0 },
      minor: { total: 0, count: 0 },
    };

    assignedThisWeek.forEach((complaint) => {
      const { assignedAt, ackAt, startAt } = resolveResponseTimestamps(complaint);
      if (!assignedAt || !startAt) return;

      const responseMs = startAt - assignedAt;
      if (responseMs < 0) return;

      const responseMinutes = responseMs / (1000 * 60);
      totalResponseMinutes += responseMinutes;
      responseCount += 1;

      const severity = complaint.severity || 'major';
      if (responseMinutesBySeverity[severity]) {
        responseMinutesBySeverity[severity].total += responseMinutes;
        responseMinutesBySeverity[severity].count += 1;
      }

      if (ackAt) {
        const assignToAckMs = ackAt - assignedAt;
        const ackToStartMs = startAt - ackAt;
        if (assignToAckMs >= 0) {
          totalAssignToAckMinutes += assignToAckMs / (1000 * 60);
          assignToAckCount += 1;
        }
        if (ackToStartMs >= 0) {
          totalAckToStartMinutes += ackToStartMs / (1000 * 60);
          ackToStartCount += 1;
        }
      }
    });

    const toHours = (minutes, count) =>
      count > 0 ? Math.round((minutes / count / 60) * 10) / 10 : 0;

    const avgResponseTimeHours = toHours(totalResponseMinutes, responseCount);
    const avgResponseTimeMinutes =
      responseCount > 0 ? Math.round((totalResponseMinutes / responseCount) * 10) / 10 : 0;
    const avgAssignToAckHours = toHours(totalAssignToAckMinutes, assignToAckCount);
    const avgAckToStartHours = toHours(totalAckToStartMinutes, ackToStartCount);

    const responseTimeBySeverity = {
      critical: toHours(
        responseMinutesBySeverity.critical.total,
        responseMinutesBySeverity.critical.count
      ),
      major: toHours(
        responseMinutesBySeverity.major.total,
        responseMinutesBySeverity.major.count
      ),
      minor: toHours(
        responseMinutesBySeverity.minor.total,
        responseMinutesBySeverity.minor.count
      ),
    };

    // Determine trend (comparing to previous week)
    const lastWeekResolved = complaints.filter(c => {
      if (c.status !== 'closed') return false;
      if (!c.closed_at) return false;
      const resolvedDate = new Date(c.closed_at);
      return resolvedDate >= prevWeekStart && resolvedDate <= prevWeekEnd;
    }).length;

    let trend = 'stable';
    let trendPercent = 0;

    if (lastWeekResolved > 0) {
      trendPercent = Math.round(((resolvedThisWeek.length - lastWeekResolved) / lastWeekResolved) * 100);
      if (trendPercent > 10) trend = 'improving';
      else if (trendPercent < -10) trend = 'declining';
    } else if (resolvedThisWeek.length > 0) {
      trend = 'improving';
      trendPercent = 100;
    }

    return {
      resolvedThisWeek: resolvedThisWeek.length,
      bySeverity,
      avgResolutionTime,
      avgTicketAgeDays,
      avgTicketAgeHours,
      ticketAgeBySevertity,
      repeatIssueCount,
      topRepeatIssues: topRepeatIssuesDisplay,
      onTimeCompletionRate,
      resolutionTimeBySeverity,
      onTimeRateBySeverity,
      avgResponseTimeMinutes,
      avgResponseTimeHours,
      avgAssignToAckHours,
      avgAckToStartHours,
      responseTimeBySeverity,
      trend,
      trendPercent: Math.abs(trendPercent)
    };
  };

  const calculateMaintenanceMetrics = () => {
    const { start: weekStart, end: weekEnd } = getThisWeekRange();
    const { start: prevWeekStart, end: prevWeekEnd } = getPreviousWeekRange();

    // Get maintenance logs from devices
    const allMaintenanceLogs = [];
    devices.forEach(device => {
      if (device.maintenance_logs && Array.isArray(device.maintenance_logs)) {
        allMaintenanceLogs.push(...device.maintenance_logs);
      }
    });

    // Filter completed maintenance from this week
    const completedThisWeek = allMaintenanceLogs.filter(log => {
      if (!log.maintenance_date) return false;
      const maintenanceDate = new Date(log.maintenance_date);
      return maintenanceDate >= weekStart && maintenanceDate <= weekEnd;
    });

    // Separate by maintenance type (ppm, calibration, compliance)
    const byType = {
      ppm: completedThisWeek.filter(log =>
        log.maintenance_type && log.maintenance_type === 'PPM'
      ),
      calibration: completedThisWeek.filter(log =>
        log.maintenance_type && log.maintenance_type === 'Calibration'
      ),
      compliance: completedThisWeek.filter(log =>
        log.maintenance_type && log.maintenance_type === 'Compliance'
      )
    };

    // Calculate average hours spent on maintenance work
    let avgCompletionTime = 0;
    let totalHoursSpent = 0;
    if (completedThisWeek.length > 0) {
      completedThisWeek.forEach(log => {
        if (log.hours_spent) {
          totalHoursSpent += parseFloat(log.hours_spent);
        }
      });
      avgCompletionTime = Math.round(totalHoursSpent / completedThisWeek.length * 10) / 10;
    }

    // Calculate total hours spent on maintenance this week
    const totalMaintenanceHours = Math.round(totalHoursSpent * 10) / 10;

    // Calculate on-time completion by type
    // For maintenance logs, we consider it "on-time" if maintenance_date <= next_due_date
    const calculateOnTimeRate = (logs) => {
      if (logs.length === 0) return 0;
      let onTimeCount = 0;
      logs.forEach(log => {
        if (log.maintenance_date && log.next_due_date) {
          const maintenanceDate = new Date(log.maintenance_date);
          const nextDueDate = new Date(log.next_due_date);
          // If maintenance was done before or on the next due date, it's on time
          if (maintenanceDate <= nextDueDate) {
            onTimeCount++;
          }
        } else {
          // If missing dates, assume on-time
          onTimeCount++;
        }
      });
      return Math.round((onTimeCount / logs.length) * 100);
    };

    const onTimeByType = {
      ppm: calculateOnTimeRate(byType.ppm),
      calibration: calculateOnTimeRate(byType.calibration),
      compliance: calculateOnTimeRate(byType.compliance)
    };

    // Overall on-time rate
    let overallOnTimeCount = 0;
    completedThisWeek.forEach(log => {
      if (log.maintenance_date && log.next_due_date) {
        const maintenanceDate = new Date(log.maintenance_date);
        const nextDueDate = new Date(log.next_due_date);
        if (maintenanceDate <= nextDueDate) {
          overallOnTimeCount++;
        }
      } else {
        overallOnTimeCount++;
      }
    });

    const onTimeCompletionRate = completedThisWeek.length > 0
      ? Math.round((overallOnTimeCount / completedThisWeek.length) * 100)
      : 0;

    // Count unique devices maintained
    const devicesMaintained = new Set(completedThisWeek.map(log => log.device_id)).size;

    // Calculate trend
    const lastWeekCompletedLogs = allMaintenanceLogs.filter(log => {
      if (!log.maintenance_date) return false;
      const maintenanceDate = new Date(log.maintenance_date);
      return maintenanceDate >= prevWeekStart && maintenanceDate <= prevWeekEnd;
    });
    const lastWeekCompleted = lastWeekCompletedLogs.length;

    let trend = 'stable';
    let trendPercent = 0;

    if (lastWeekCompleted > 0) {
      trendPercent = Math.round(((completedThisWeek.length - lastWeekCompleted) / lastWeekCompleted) * 100);
      if (trendPercent > 10) trend = 'improving';
      else if (trendPercent < -10) trend = 'declining';
    } else if (completedThisWeek.length > 0) {
      trend = 'improving';
      trendPercent = 100;
    }

    return {
      completedThisWeek: completedThisWeek.length,
      byType: {
        ppm: byType.ppm.length,
        calibration: byType.calibration.length,
        compliance: byType.compliance.length
      },
      avgCompletionTime,
      totalMaintenanceHours,
      onTimeCompletionRate,
      onTimeByType,
      devicesMaintained,
      previousWeekCompleted: lastWeekCompleted,
      trend,
      trendPercent: Math.abs(trendPercent)
    };
  };

  const calculateOverallMetrics = (complaintMetrics, maintenanceMetrics) => {
    const { start: prevWeekStart, end: prevWeekEnd } = getPreviousWeekRange();

    const completedThisWeek =
      complaintMetrics.resolvedThisWeek + maintenanceMetrics.completedThisWeek;

    const complaintHours = Math.round(
      (complaintMetrics.avgResolutionTime * complaintMetrics.resolvedThisWeek) * 10
    ) / 10;
    const maintenanceHours = maintenanceMetrics.totalMaintenanceHours || 0;
    const totalWorkHours = Math.round((complaintHours + maintenanceHours) * 10) / 10;

    let avgCompletionTime = 0;
    if (completedThisWeek > 0) {
      avgCompletionTime = Math.round((totalWorkHours / completedThisWeek) * 10) / 10;
    }

    const totalOnTime =
      (complaintMetrics.onTimeCompletionRate * complaintMetrics.resolvedThisWeek) +
      (maintenanceMetrics.onTimeCompletionRate * maintenanceMetrics.completedThisWeek);

    const onTimeCompletionRate = completedThisWeek > 0
      ? Math.round(totalOnTime / (completedThisWeek * 100) * 100)
      : 0;

    const prevComplaints = complaints.filter(c => {
      if (c.status !== 'closed' || !c.closed_at) return false;
      const resolvedDate = new Date(c.closed_at);
      return resolvedDate >= prevWeekStart && resolvedDate <= prevWeekEnd;
    }).length;
    const previousWeekTotal = prevComplaints + (maintenanceMetrics.previousWeekCompleted || 0);

    let trend = 'stable';
    let trendPercent = 0;

    if (previousWeekTotal > 0) {
      trendPercent = Math.round(((completedThisWeek - previousWeekTotal) / previousWeekTotal) * 100);
      if (trendPercent > 10) trend = 'improving';
      else if (trendPercent < -10) trend = 'declining';
    } else if (completedThisWeek > 0) {
      trend = 'improving';
      trendPercent = 100;
    }

    return {
      completedThisWeek,
      avgCompletionTime,
      onTimeCompletionRate,
      totalWorkHours,
      complaintsResolved: complaintMetrics.resolvedThisWeek,
      maintenanceCompleted: maintenanceMetrics.completedThisWeek,
      complaintHours,
      maintenanceHours,
      trend,
      trendPercent: Math.abs(trendPercent)
    };
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return '📈';
    if (trend === 'declining') return '📉';
    return '➡️';
  };

  const getTrendColor = (trend) => {
    if (trend === 'improving') return '#16a34a';
    if (trend === 'declining') return '#dc2626';
    return '#6b7280';
  };

  const getTrendText = (trend, trendPercent) => {
    if (trend === 'improving') return `${trendPercent}% better vs previous 7 days`;
    if (trend === 'declining') return `${trendPercent}% worse vs previous 7 days`;
    return 'Stable vs previous 7 days';
  };

  const safeRenderValue = (value, defaultValue = 0) => {
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : defaultValue;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return defaultValue;
      const numeric = parseFloat(trimmed.replace(/[^0-9+\-.]/g, ''));
      return Number.isNaN(numeric) ? defaultValue : numeric;
    }
    return defaultValue;
  };

  const goComplaints = () => {
    if (typeof onNavigateToComplaints === 'function') onNavigateToComplaints();
  };
  const goDevices = () => {
    if (typeof onNavigateToDevices === 'function') onNavigateToDevices();
  };
  const goWorkLogs = () => {
    if (typeof onNavigateToWorkLogs === 'function') onNavigateToWorkLogs();
  };
  const goWorkQueue = () => {
    const el = typeof document !== 'undefined'
      ? document.getElementById('section-workQueue')
      : null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    goComplaints();
  };

  const renderSeveritySplit = (counts, { unit = '', show = true } = {}) => {
    if (!show) return null;
    const crit = safeRenderValue(counts?.critical);
    const maj = safeRenderValue(counts?.major);
    const min = safeRenderValue(counts?.minor);
    return (
      <div className="performance-split" aria-label="By severity">
        <span className="split-pill severity-critical" title="Critical">Critical {crit}{unit}</span>
        <span className="split-pill severity-major" title="Major">Major {maj}{unit}</span>
        <span className="split-pill severity-minor" title="Minor">Minor {min}{unit}</span>
      </div>
    );
  };

  const renderTypeSplit = (byType, { asPercent = false, show = true } = {}) => {
    if (!show) return null;
    const suffix = asPercent ? '%' : '';
    return (
      <div className="performance-split" aria-label="By maintenance type">
        <span className="split-pill type-ppm" title="Preventive maintenance">
          PPM {safeRenderValue(byType?.ppm)}{suffix}
        </span>
        <span className="split-pill type-calibration" title="Calibration">
          Calibration {safeRenderValue(byType?.calibration)}{suffix}
        </span>
        <span className="split-pill type-compliance" title="Compliance">
          Compliance {safeRenderValue(byType?.compliance)}{suffix}
        </span>
      </div>
    );
  };

  const renderMetricsCard = ({
    icon,
    value,
    label,
    sublabel,
    split = null,
    onClick = null,
    valueSuffix = '',
  }) => {
    const clickable = typeof onClick === 'function';
    const Tag = clickable ? 'button' : 'div';
    return (
      <Tag
        type={clickable ? 'button' : undefined}
        className={`performance-card${clickable ? ' performance-card--clickable' : ''}`}
        onClick={clickable ? onClick : undefined}
      >
        <div className="card-icon" aria-hidden="true">{icon}</div>
        <div className="card-content">
          <div className="metric-value">
            {safeRenderValue(value)}
            {valueSuffix}
          </div>
          <div className="metric-label">{label}</div>
          {sublabel ? <div className="metric-sublabel">{sublabel}</div> : null}
          {split}
        </div>
      </Tag>
    );
  };

  const renderEmptyHint = (message, actionLabel, onAction) => (
    <div className="performance-empty-hint">
      <p>{message}</p>
      {typeof onAction === 'function' && actionLabel ? (
        <button type="button" className="performance-empty-action" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );

  const renderTrendChip = (data) => (
    <div
      className="performance-trend-chip"
      style={{ '--trend-color': getTrendColor(data?.trend) }}
      title={getTrendText(data?.trend, data?.trendPercent)}
    >
      <span aria-hidden="true">{getTrendIcon(data?.trend)}</span>
      <span>{getTrendText(data?.trend, data?.trendPercent)}</span>
    </div>
  );

  const renderTabContent = () => {
    const data = performanceData[activeTab];

    switch (activeTab) {
      case 'complaints': {
        const resolved = safeRenderValue(data.resolvedThisWeek);
        const hasWork = resolved > 0;
        return (
          <>
            {!hasWork && renderEmptyHint(
              'No complaints resolved in the last 7 days.',
              'Open complaints',
              goComplaints
            )}
            <div className="performance-metrics-grid performance-metrics-grid--primary">
              {renderMetricsCard({
                icon: '💬',
                value: resolved,
                label: 'Resolved',
                sublabel: 'Last 7 days',
                split: renderSeveritySplit(data.bySeverity, { show: hasWork }),
                onClick: goComplaints,
              })}
              {renderMetricsCard({
                icon: '✓',
                value: data.onTimeCompletionRate,
                valueSuffix: '%',
                label: 'On-time',
                sublabel: 'Last 7 days',
                split: renderSeveritySplit(data.onTimeRateBySeverity, {
                  unit: '%',
                  show: hasWork,
                }),
              })}
              {renderMetricsCard({
                icon: '⚡',
                value: data.avgResponseTimeHours,
                valueSuffix: 'h',
                label: 'Avg response',
                sublabel: 'Assignment → start',
                split: hasWork ? (
                  <>
                    <div className="performance-stage-inline" aria-label="Response stages">
                      <span>
                        Ack <strong>{safeRenderValue(data.avgAssignToAckHours)}h</strong>
                      </span>
                      <span className="performance-stage-sep" aria-hidden="true">·</span>
                      <span>
                        After ack <strong>{safeRenderValue(data.avgAckToStartHours)}h</strong>
                      </span>
                    </div>
                    {renderSeveritySplit(data.responseTimeBySeverity, {
                      unit: 'h',
                      show: true,
                    })}
                  </>
                ) : null,
              })}
            </div>
            <div className="performance-metrics-grid performance-metrics-grid--secondary">
              {renderMetricsCard({
                icon: '⏱️',
                value: data.avgResolutionTime,
                valueSuffix: 'h',
                label: 'Avg resolve time',
                sublabel: 'Last 7 days',
                split: renderSeveritySplit(data.resolutionTimeBySeverity, {
                  unit: 'h',
                  show: hasWork,
                }),
              })}
              {renderMetricsCard({
                icon: '🔁',
                value: data.repeatIssueCount,
                label: 'Repeat issues',
                sublabel: 'Last 30 days · first report not counted',
                onClick: goComplaints,
                split: data.topRepeatIssues?.length > 0 ? (
                  <div className="performance-repeat-list">
                    {data.topRepeatIssues.slice(0, 2).map((issue, idx) => {
                      const title = (issue.title || '').trim() || 'Untitled';
                      const shortTitle = title.length > 40 ? `${title.substring(0, 40)}…` : title;
                      return (
                        <div key={idx} className="performance-repeat-item">
                          {issue.count}× complaints for same issue: {shortTitle}
                        </div>
                      );
                    })}
                  </div>
                ) : null,
              })}
            </div>
          </>
        );
      }

      case 'maintenance': {
        const completed = safeRenderValue(data.completedThisWeek);
        const hasWork = completed > 0;
        return (
          <>
            {!hasWork && renderEmptyHint(
              'No maintenance logged in the last 7 days.',
              'View My Devices',
              goDevices
            )}
            <div className="performance-metrics-grid">
              {renderMetricsCard({
                icon: '🔧',
                value: completed,
                label: 'Completed',
                sublabel: 'Last 7 days',
                split: renderTypeSplit(data.byType, { show: hasWork }),
                onClick: goWorkLogs,
              })}
              {renderMetricsCard({
                icon: '✓',
                value: data.onTimeCompletionRate,
                valueSuffix: '%',
                label: 'On-time',
                sublabel: 'Last 7 days',
                split: renderTypeSplit(data.onTimeByType, {
                  asPercent: true,
                  show: hasWork,
                }),
              })}
              {renderMetricsCard({
                icon: '⏱️',
                value: data.totalMaintenanceHours,
                valueSuffix: 'h',
                label: 'Hours',
                sublabel: hasWork
                  ? `Avg ${safeRenderValue(data.avgCompletionTime)}h per job`
                  : 'Last 7 days',
                onClick: goWorkLogs,
              })}
              {renderMetricsCard({
                icon: '🖥️',
                value: data.devicesMaintained,
                label: 'Devices',
                sublabel: 'Maintained this week',
                onClick: goDevices,
              })}
            </div>
          </>
        );
      }

      case 'overall':
      default: {
        const completed = safeRenderValue(data.completedThisWeek);
        const hasWork = completed > 0;
        return (
          <>
            {!hasWork && renderEmptyHint(
              'No completed work in the last 7 days.',
              'Open work queue',
              goWorkQueue
            )}
            <div className="performance-metrics-grid">
              {renderMetricsCard({
                icon: '📋',
                value: completed,
                label: 'Tasks done',
                sublabel: 'Last 7 days',
                onClick: goComplaints,
              })}
              {renderMetricsCard({
                icon: '⏱️',
                value: data.totalWorkHours,
                valueSuffix: 'h',
                label: 'Total hours',
                sublabel: 'Logged this week',
                onClick: goWorkLogs,
              })}
              {renderMetricsCard({
                icon: '⌛',
                value: data.avgCompletionTime,
                valueSuffix: 'h',
                label: 'Avg hours',
                sublabel: 'Per task',
              })}
              {renderMetricsCard({
                icon: '✓',
                value: data.onTimeCompletionRate,
                valueSuffix: '%',
                label: 'On-time',
                sublabel: 'Last 7 days',
              })}
            </div>
            <div className="performance-mix-strip" aria-label="Work mix this week">
              <button
                type="button"
                className="performance-mix-item"
                onClick={() => setActiveTab('complaints')}
              >
                <span className="mix-label">Complaints</span>
                <span className="mix-value">{safeRenderValue(data.complaintsResolved)}</span>
                <span className="mix-meta">{safeRenderValue(data.complaintHours)}h</span>
              </button>
              <button
                type="button"
                className="performance-mix-item"
                onClick={() => setActiveTab('maintenance')}
              >
                <span className="mix-label">Maintenance</span>
                <span className="mix-value">{safeRenderValue(data.maintenanceCompleted)}</span>
                <span className="mix-meta">{safeRenderValue(data.maintenanceHours)}h</span>
              </button>
            </div>
          </>
        );
      }
    }
  };

  if (loading) {
    return (
      <div className="personal-performance-widget">
        <div className="widget-loading">
          <div className="spinner-small"></div>
          <p>Loading performance data...</p>
        </div>
      </div>
    );
  }

  const activeTrend = performanceData[activeTab];

  return (
    <div className="personal-performance-widget">
      <div className="performance-widget-header">
        <div className="performance-title-block">
          <h3 className="dashboard-section-title">📊 My Performance</h3>
          <div className="performance-header-meta">
            <div className="performance-date-badge" title="Rolling 7-day window (UTC)">
              📅 {getThisWeekDateRangeLabel()}
            </div>
            {!error && renderTrendChip(activeTrend)}
          </div>
        </div>
        <div className="performance-tabs" role="tablist" aria-label="Performance views">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'overall'}
            className={`performance-tab-btn ${activeTab === 'overall' ? 'active' : ''}`}
            onClick={() => setActiveTab('overall')}
          >
            Overall
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'complaints'}
            className={`performance-tab-btn ${activeTab === 'complaints' ? 'active' : ''}`}
            onClick={() => setActiveTab('complaints')}
          >
            Complaints
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'maintenance'}
            className={`performance-tab-btn ${activeTab === 'maintenance' ? 'active' : ''}`}
            onClick={() => setActiveTab('maintenance')}
          >
            Maintenance
          </button>
        </div>
      </div>

      {error ? (
        <div className="widget-error">
          <p>⚠️ {error}</p>
        </div>
      ) : (
        renderTabContent()
      )}
    </div>
  );
};

export default PersonalPerformanceWidget;
